<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    location="center"
    :disabled="disabled"
    :scroll-strategy="'none'"
  >
    <template v-slot:activator="{ props }">
      <v-text-field
        v-model="formattedDateTime"
        :class="customClass"
        :density="density"
        :label="label"
        :rules="rules"
        :variant="variant"
        :placeholder="placeholder"
        :disabled="disabled"
        :error="error"
        :error-messages="errorMessages"
        hide-details="auto"
        prepend-inner-icon="mdi-calendar-clock"
        readonly
        v-bind="props"
        clearable
        @click:clear="clearDateTime"
      />
    </template>
    <div class="position-relative datetime-picker-container">
      <v-card>
        <v-card-text class="pa-0">
          <v-date-picker
            v-model="dateModel"
            :color="color"
            :width="xs ? width - 30 : 'auto'"
            height="auto"
            show-adjacent-months
            title=""
            :min="new Date().toISOString().slice(0,10)"
            @update:modelValue="handleDateChange"
          />

          <div class="time-picker-section pa-4">
            <div class="text-subtitle-1 mb-2 font-weight-medium">
              Select Time
            </div>
            <div class="d-flex align-center justify-center time-inputs">
              <v-select
                v-model="hours"
                :items="hoursItems"
                label="Hour"
                variant="outlined"
                density="compact"
                class="time-input-field mr-2"
                hide-details
              ></v-select>

              <!-- TODO: This could all be tweked -->
              <span class="time-separator mx-1">:</span>

              <v-select
                v-model="minutes"
                :items="minutesItems"
                label="Minute"
                variant="outlined"
                density="compact"
                class="time-input-field ml-2"
                hide-details
              ></v-select>

              <v-select
                v-if="!use24HourFormat"
                v-model="period"
                :items="['AM', 'PM']"
                variant="outlined"
                density="compact"
                class="period-selector ml-2"
                hide-details
              ></v-select>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="justify-end pa-3">
          <v-btn variant="text" @click="menu = false"> Cancel </v-btn>
          <v-btn :color="color" @click="confirmDateTime"> OK </v-btn>
        </v-card-actions>
      </v-card>

      <v-btn
        class="position-absolute top-0 right-0"
        color="white"
        icon="mdi-close"
        size="small"
        variant="text"
        @click="menu = false"
      ></v-btn>
    </div>
  </v-menu>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps({
  modelValue: {
    type: [Date, String],
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: 'primary',
  },
  customClass: {
    type: String,
    default: '',
  },
  rules: {
    type: Array,
    default: () => [],
  },
  variant: {
    type: String,
    default: 'outlined',
  },
  density: {
    type: String,
    default: 'compact',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  format: {
    type: String,
    default: 'DD/MM/YYYY HH:mm',
  },
  placeholder: {
    type: String,
    default: 'Select date and time',
  },
  use24HourFormat: {
    type: Boolean,
    default: false,
  },
  inFuture: {
    type: Boolean,
    default: false,
  },
  timeStep: {
    type: Number,
    default: 5,
  },
  error: {
    type: Boolean,
  },
  errorMessages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { width, height, xs } = useDisplay();
const menu = ref(false);
const dateModel = ref(null);
const hours = ref(12);
const minutes = ref(0);
const period = ref('AM');
const formattedDateTime = ref('');

// Generate hours items based on 12-hour or 24-hour format
const hoursItems = computed(() => {
  if (props.use24HourFormat) {
    return Array.from({ length: 24 }, (_, i) => ({
      title: String(i).padStart(2, '0'),
      value: i,
    }));
  } else {
    return Array.from({ length: 12 }, (_, i) => {
      const val = i === 0 ? 12 : i;
      return {
        title: String(val).padStart(2, '0'),
        value: val,
      };
    });
  }
});

const minutesItems = computed(() => {
  const items = [];
  for (let i = 0; i < 60; i += props.timeStep) {
    items.push({
      title: String(i).padStart(2, '0'),
      value: i,
    });
  }
  return items;
});

const todayDate = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (!newVal) {
      resetFields();
      return;
    }

    try {
      const dateObj = newVal instanceof Date ? newVal : new Date(newVal);

      if (isNaN(dateObj.getTime())) {
        throw new Error('Invalid date');
      }

      dateModel.value = dateObj;
      let hours24 = dateObj.getHours();

      if (props.use24HourFormat) {
        hours.value = hours24;
      } else {
        hours.value = hours24 % 12 || 12;
        period.value = hours24 >= 12 ? 'PM' : 'AM';
      }

      minutes.value =
        Math.floor(dateObj.getMinutes() / props.timeStep) * props.timeStep;

      formattedDateTime.value = formatDateTime(dateObj);
    } catch (e) {
      console.error('Error parsing datetime:', e);
      resetFields();
    }
  },
  { immediate: true }
);

// Reset all
function resetFields() {
  dateModel.value = null;
  hours.value = 12;
  minutes.value = 0;
  period.value = 'AM';
  formattedDateTime.value = '';
}

function handleDateChange(newDate) {
  dateModel.value = newDate;
}

function confirmDateTime() {
  if (!dateModel.value) return;

  try {
    const selectedDate = new Date(dateModel.value);

    // convert 12hour format to 24hour
    let hours24 = hours.value;
    if (!props.use24HourFormat && period.value === 'PM' && hours.value !== 12) {
      hours24 = hours.value + 12;
    } else if (
      !props.use24HourFormat &&
      period.value === 'AM' &&
      hours.value === 12
    ) {
      hours24 = 0;
    }

    const dateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      hours24,
      minutes.value
    );

    formattedDateTime.value = formatDateTime(dateTime);
    emit('update:modelValue', dateTime);
    emit('change', dateTime);
    menu.value = false;
  } catch (e) {
    console.error('Error setting datetime:', e);
  }
}

function formatDateTime(inputDate) {
  if (!inputDate) return '';

  try {
    const parsedDate =
      inputDate instanceof Date ? inputDate : new Date(inputDate);
    if (isNaN(parsedDate.getTime())) return '';

    const day = String(parsedDate.getDate()).padStart(2, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const year = parsedDate.getFullYear();

    let hours24 = parsedDate.getHours();
    let hours12 = hours24 % 12 || 12;
    const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
    const ampm = hours24 >= 12 ? 'PM' : 'AM';

    if (props.format === 'M-DD-YYYY hh:mm A') {
      return `${parsedDate.getMonth() + 1}-${day}-${year} ${String(
        hours12
      ).padStart(2, '0')}:${minutes} ${ampm}`;
    } else if (props.format === 'DD/MM/YYYY HH:mm') {
      return `${day}/${month}/${year} ${String(hours24).padStart(
        2,
        '0'
      )}:${minutes}`;
    } else if (props.format === 'YYYY-MM-DD HH:mm') {
      return `${year}-${month}-${day} ${String(hours24).padStart(
        2,
        '0'
      )}:${minutes}`;
    } else if (props.format === 'MM/DD/YYYY hh:mm A') {
      return `${month}/${day}/${year} ${String(hours12).padStart(
        2,
        '0'
      )}:${minutes} ${ampm}`;
    } else {
      // Default format
      return `${day}/${month}/${year} ${String(hours24).padStart(
        2,
        '0'
      )}:${minutes}`;
    }
  } catch (e) {
    console.error('Error formatting datetime:', e);
    return '';
  }
}

function clearDateTime() {
  resetFields();
  emit('update:modelValue', null);
  emit('change', null);
}

onMounted(() => {
  if (props.modelValue) {
    const dateObj =
      props.modelValue instanceof Date
        ? props.modelValue
        : new Date(props.modelValue);
    formattedDateTime.value = formatDateTime(dateObj);
  }
});
</script>

<style>
.v-overlay__content:has(> .datetime-picker-container) {
  min-width: auto !important;
  z-index: 3000 !important;
}

.v-overlay--active {
  z-index: 3000 !important;
}

.v-picker-title {
  padding: 0 !important;
}

.position-absolute {
  position: absolute !important;
}

.position-relative {
  position: relative !important;
}

.top-0 {
  top: 0 !important;
}

.right-0 {
  right: 0 !important;
}

@media only screen and (max-width: 600px) {
  .v-overlay__content:has(> .datetime-picker-container) {
    left: 0 !important;
  }
}

.time-picker-section {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.time-inputs {
  margin: 10px 0;
}

.time-input-field {
  width: 80px;
}

.time-separator {
  font-size: 20px;
  font-weight: bold;
}

.period-selector {
  width: 70px;
}
</style>
